'use client';

import { motion, AnimatePresence } from 'motion/react';
import { X } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import type { ActiveFilters, FilterCategories, FilterKey } from './filter-types';
import { FILTER_KEYS } from './filter-types';

interface ActiveFilterChipsProps {
  categories: FilterCategories;
  activeFilters: ActiveFilters;
  onRemove: (key: FilterKey, handle: string) => void;
  onClearAll: () => void;
}

/** Removable chips for the currently applied taxonomy filters, above the grid. */
export function ActiveFilterChips({
  categories,
  activeFilters,
  onRemove,
  onClearAll,
}: ActiveFilterChipsProps) {
  const t = useTranslations('shop.filterBar');

  // handle → display name, so a chip shows "Ocean" not "ocean-abstract".
  const handleToName = useMemo(() => {
    const map: Record<string, string> = {};
    for (const key of FILTER_KEYS) {
      for (const option of categories[key] ?? []) map[option.handle] = option.name;
    }
    return map;
  }, [categories]);

  const chips = FILTER_KEYS.flatMap(key =>
    (activeFilters[key] ?? []).map(handle => ({ key, handle })),
  );

  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 pt-4">
      <span className="text-xs text-gray-500 dark:text-gray-400">{t('activeFilters')}</span>
      <AnimatePresence initial={false}>
        {chips.map(({ key, handle }) => (
          <motion.button
            key={`${key}-${handle}`}
            layout
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            onClick={() => onRemove(key, handle)}
            className="flex min-h-8 items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:hover:bg-amber-900/50"
          >
            {handleToName[handle] || handle}
            <X className="h-3 w-3" />
          </motion.button>
        ))}
      </AnimatePresence>
      <button
        onClick={onClearAll}
        className="text-xs text-gray-500 underline transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
      >
        {t('clearAll')}
      </button>
    </div>
  );
}
